import { queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import useSession from '@/hooks/sessions/use-session';
import useUserPlatform from '@/hooks/use-user-platform';
import {
  type ConversationSessionSettingType,
  type ConversationType,
  conversationSessionSettingScheme,
} from '@/models/conversations/conversation-session';
import { generateResponseScheme, type RespondedWith, ResponseError } from '@/models/response';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import { addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { generateSessionKey } from '@/utils/session';

export const generateSessionSettingsQueryOptions = (type: ConversationType, id: Nullable<number>) =>
  queryOptions({
    queryKey: ['/GetChatSessionByID', type, id],
    queryFn: id
      ? async ({ signal }) => {
          const response = await fetcher<RespondedWith<ConversationSessionSettingType>>(
            '/GetChatSessionByID',
            { [type === 'session' ? 'ChatSessionID' : 'GroupID']: id },
            {
              signal,
              schema: generateResponseScheme(conversationSessionSettingScheme),
              validResponseCodes: [0],
            },
          );

          if (response.Data) {
            const key = generateSessionKey(type, id);
            useConversationSessionStore.getState().updateSession(key, {
              IsNotDisturb: response.Data.IsNotDisturb,
              IsTop: response.Data.IsTop,
            });
          }

          return response?.Data ?? null;
        }
      : skipToken,
    staleTime: 0,
  });

export default function useSessionSettings(type: ConversationType, id: Nullable<number>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery(generateSessionSettingsQueryOptions(type, id));
  const { isEmbedded } = useUserPlatform();
  const { session } = useSession(type, id);
  const { dispatch } = useCommDispatcher();

  useEffect(() => {
    if (query.error && ResponseError.isResponseError(query.error)) {
      if (query.error.code === 1) {
        addTranslateToast({
          type: 'error',
          message: 'conversation.session-not-available',
        });

        Promise.allSettled([
          queryClient.invalidateQueries(friendsQueryOptions),
          queryClient.invalidateQueries(groupsQueryOptions),
          queryClient.invalidateQueries(allChatSessionQueryOptions),
        ]).catch(console.warn);
        if (id) useMessageStore.getState().deleteBySessionId(type, id).catch(console.warn);

        if (isEmbedded) {
          dispatch({
            event: 'leave-session',
            data: type === 'group' ? { type: 'group', id: id ?? 0 } : { type: 'user', id: session?.UserID ?? 0 },
          });
        } else {
          navigate(ROUTES.MESSAGES.$path());
        }
      }
    }
  }, [query.error]);

  return {
    session,
    setting: query.data,
    ...query,
  };
}
